import { orderBy } from 'lodash'
import { ChangeEvent, ReactNode, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from 'react-query'
import { useNavigate } from 'react-router'
import IWButton from 'shared/components/thunderbolt/IWButton'
import IWDropdown, {
  DropdownValueProps,
} from 'shared/components/thunderbolt/IWDropdown'
import IWGenericCard from 'shared/components/thunderbolt/IWGenericCard'
import IWTextInput from 'shared/components/thunderbolt/IWTextInput'
import { useToast } from 'shared/components/thunderbolt/IWToastContext'
import IWToggle from 'shared/components/thunderbolt/IWToggle'
import IWTypography from 'shared/components/thunderbolt/IWTypography'
import {
  createInternalBilateralContract,
  getCredentials,
} from 'shared/loadSchedulingClient'
import styled from 'styled-components'

const Wrapper = styled.div`
  display: flex;
  justify-content: left;
  flex-direction: column;
  gap: 1rem;
`

const StyledSectionRow = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  justify-content: left;
  flex-direction: column;
  margin: 0 auto 0 0;

  & > * {
    flex: 0 0 calc(10% - 0.75rem); /* 25% width for 4 items, accounting for gap */
  }
`

const StyledFooter = styled.footer`
  display: flex;
  align-items: center;
  justify-content: left;
  gap: 0.5rem;
`

const PreferencesSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`

const CardContent = styled.div`
  display: flex;
  padding: 1rem;
  flex-direction: column;
  gap: 1rem;
`

const StyledInfoBlock = styled.div`
  display: flex;
  flex-grow: 1;
  flex-direction: column;
  gap: 0.5rem;
`

const StyledInfoSection = styled.div`
  display: flex;
  flex-direction: row;
  gap: 1rem;
`

const MAX_UTILITY_LENGTH = 30

const Section = ({
  title,
  children,
}: {
  title?: string
  children: ReactNode
}) => {
  return (
    <PreferencesSection>
      {title && (
        <IWTypography size="lg" weight="medium">
          {title}
        </IWTypography>
      )}
      <IWGenericCard>
        <CardContent>
          <StyledInfoSection>
            <StyledInfoBlock>{children}</StyledInfoBlock>
          </StyledInfoSection>
        </CardContent>
      </IWGenericCard>
    </PreferencesSection>
  )
}

export default function LsAdminCreateIbtContracts() {
  const navigate = useNavigate()
  const toast = useToast()
  const { t } = useTranslation()
  const [isLoading, setIsLoading] = useState(false)
  const [credentialOptions, setCredentialOptions] = useState<
    DropdownValueProps[]
  >([])
  const [selectedCredential, setSelectedCredential] =
    useState<DropdownValueProps>()
  const [selectedBuyOrSell, setSelectedBuyOrSell] =
    useState<DropdownValueProps>()
  const [isActive, setIsActive] = useState(false)
  const [contractId, setContractId] = useState('')
  const [buyerName, setBuyerName] = useState('')
  const [sellerName, setSellerName] = useState('')
  const [buyerQseCode, setBuyerQseCode] = useState('')
  const [sellerQseCode, setSellerQseCode] = useState('')
  const [counterparty, setCounterparty] = useState('')

  const buyOrSellOptions = [
    { label: 'BUY', value: 'BUY' },
    { label: 'SELL', value: 'SELL' },
  ]

  const { data: credentials } = useQuery({
    queryKey: ['ls-admin-credentials'],
    queryFn: async () => {
      return getCredentials(true)
    },
  })

  const handleNavigateBack = () => {
    navigate('/admin/load-scheduling/ibt-contracts')
  }

  const hasErrors = () => {
    if (!counterparty) {
      return true
    }

    if (!buyerName) {
      return true
    }

    if (!buyerQseCode) {
      return true
    }

    if (!sellerName) {
      return true
    }

    if (!sellerQseCode) {
      return true
    }

    if (!selectedBuyOrSell) {
      return true
    }

    if (!selectedCredential) {
      return true
    }

    return false
  }

  const handleCreate = async () => {
    if (hasErrors()) {
      return
    }

    if (!selectedCredential?.value || !selectedBuyOrSell?.value) {
      return
    }

    setIsLoading(true)

    try {
      await createInternalBilateralContract({
        contractId,
        credentialId: selectedCredential?.value,
        sellerName,
        buyerName,
        buyOrSell: selectedBuyOrSell?.value,
        buyerQseCode,
        sellerQseCode,
        counterparty,
        isActive,
      })

      setIsLoading(false)

      handleNavigateBack()
    } catch (error) {
      toast.alert(error?.response?.data, {
        canDismiss: true,
        duration: 10_000,
      })
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (credentials && credentials.length) {
      const credentialDropdownValues = orderBy(credentials, [
        'market',
        'brand',
        'locationName',
        'location',
      ])
        .filter((c) => c.isActive)
        .map((c) => ({
          label: `${c.market} - ${c.brand} - ${c.locationName} - ${c.location}`,
          value: c.credentialId,
        }))

      setCredentialOptions(credentialDropdownValues)
      setSelectedCredential(credentialDropdownValues[0])
    }
  }, [credentials])

  return (
    <main>
      <Wrapper>
        <Section title={t('loadScheduling.labels.ibtContracts')}>
          <StyledSectionRow>
            <IWDropdown
              fullWidth
              required
              label={t('loadScheduling.labels.orgCredentials')}
              options={credentialOptions}
              value={selectedCredential}
              disabled={credentialOptions?.length === 0}
              hasError={!selectedCredential}
              onChange={(value: DropdownValueProps) => {
                setSelectedCredential(value)
              }}
            />

            <IWTextInput
              label={t('loadScheduling.labels.contractId')}
              maxLength={MAX_UTILITY_LENGTH}
              value={contractId}
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                setContractId(e.target.value)
              }}
            />

            <IWTextInput
              required
              label={t('loadScheduling.labels.counterparty')}
              maxLength={MAX_UTILITY_LENGTH}
              value={counterparty}
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                setCounterparty(e.target.value)
              }}
              hasError={!counterparty || !counterparty.length}
            />

            <IWDropdown
              fullWidth
              required
              label={t('loadScheduling.labels.selectBuyOrSell')}
              options={buyOrSellOptions}
              value={selectedBuyOrSell}
              disabled={buyOrSellOptions?.length === 0}
              hasError={!selectedBuyOrSell}
              onChange={(value: DropdownValueProps) => {
                setSelectedBuyOrSell(value)
              }}
            />
          </StyledSectionRow>
        </Section>

        <Section title={t('loadScheduling.labels.buyerInfo')}>
          <StyledSectionRow>
            <IWTextInput
              required
              label={t('loadScheduling.labels.buyerName')}
              maxLength={MAX_UTILITY_LENGTH}
              value={buyerName}
              hasError={!buyerName || !buyerName.length}
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                setBuyerName(e.target.value)
              }}
            />

            <IWTextInput
              required
              label={t('loadScheduling.labels.buyerQSECode')}
              maxLength={MAX_UTILITY_LENGTH}
              value={buyerQseCode}
              hasError={!buyerQseCode || !buyerQseCode.length}
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                setBuyerQseCode(e.target.value)
              }}
            />
          </StyledSectionRow>
        </Section>

        <Section title={t('loadScheduling.labels.sellerInfo')}>
          <StyledSectionRow>
            <IWTextInput
              required
              label={t('loadScheduling.labels.sellerName')}
              maxLength={MAX_UTILITY_LENGTH}
              value={sellerName}
              hasError={!sellerName || !sellerName.length}
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                setSellerName(e.target.value)
              }}
            />

            <IWTextInput
              required
              label={t('loadScheduling.labels.sellerQSECode')}
              maxLength={MAX_UTILITY_LENGTH}
              value={sellerQseCode}
              hasError={!sellerQseCode || !sellerQseCode.length}
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                setSellerQseCode(e.target.value)
              }}
            />
          </StyledSectionRow>
        </Section>

        <Section title={t('loadScheduling.labels.settings')}>
          <IWToggle
            id="ls-new-ibt-contract-is-active"
            label={t('loadScheduling.labels.isActive')}
            checked={isActive}
            onChange={() => {
              setIsActive((p) => !p)
            }}
          />
        </Section>

        <StyledFooter>
          <IWButton color="grey" variant="outline" onClick={handleNavigateBack}>
            {t('button.cancel')}
          </IWButton>

          <IWButton onClick={handleCreate} disabled={hasErrors() || isLoading}>
            {t('button.create')}
          </IWButton>
        </StyledFooter>
      </Wrapper>
    </main>
  )
}
